import { create } from 'zustand';
import { AutomationRun, getOpenAutomationRuns } from '../services/costPrice/automationRunService';
import { CostPriceAnalysis, fetchCostPriceAnalysesByRestaurant, fetchCostPriceAnalysesForAllRestaurants } from '../services/costPrice/costPriceService';
import { CustomIngredient, fetchCustomIngredientsByRestaurant } from '../services/customIngredients/customIngredientsService';
import { ALL_RESTAURANTS_ID } from './appUserDefaultStore';
import type { CostPriceClipboard } from '../utils/costPriceClipboard';

// 'addons' is system-managed: it surfaces CHOICE-typed analyses (shared per-choice
// cost prices used by BYO dishes). User-pickable creation paths never assign CHOICE.
export type CostPriceActiveTab = 'all' | 'food' | 'drinks' | 'addons';

interface CostPriceStore {
  ingredientClipboard: CostPriceClipboard | null;
  setIngredientClipboard: (clipboard: CostPriceClipboard | null) => void;
  automationRuns: AutomationRun[];
  automationRunsRestaurantId: string | null;
  automationRunsError: string | null;
  isLoadingAutomationRuns: boolean;
  loadAutomationRuns: (restaurantId: string) => Promise<void>;
  removeAutomationRun: (id: string) => void;
  patchOne: (id: string, partial: Partial<CostPriceAnalysis>) => void;
  costPriceList: CostPriceAnalysis[];
  customIngredients: CustomIngredient[];
  isLoading: boolean;
  isLoadingCustomIngredients: boolean;
  error: string | null;
  customIngredientsError: string | null;
  currentRestaurantId: string | null;
  customIngredientsRestaurantId: string | null;
  expandedCategories: Set<string>;

  // Active tab for cost price page (All/Food/Drinks)
  activeTab: CostPriceActiveTab;
  setActiveTab: (tab: CostPriceActiveTab) => void;

  // Add ingredient filters (for From Orders tab)
  addIngredientSupplierFilters: string[];
  setAddIngredientSupplierFilters: (filters: string[]) => void;
  addIngredientCategoryFilters: string[];
  setAddIngredientCategoryFilters: (filters: string[]) => void;

  // Cross-restaurant sourcing: the source restaurants selected in the add-ingredient
  // picker, SHARED across the From Orders / Custom / Sub recipes tabs (and surviving
  // tab switches). Default = [currentRestaurantId] (set when the picker mounts).
  addIngredientSourceRestaurantIds: string[];
  setAddIngredientSourceRestaurantIds: (ids: string[]) => void;

  load: (restaurantId: string, options?: { force?: boolean; isCurrent?: () => boolean }) => Promise<void>;
  loadCustomIngredients: (restaurantId: string, options?: { force?: boolean }) => Promise<void>;
  byId: (id: string) => CostPriceAnalysis | undefined;
  upsertOne: (analysis: CostPriceAnalysis) => void;
  removeOne: (id: string) => void;
  customIngredientById: (id: string) => CustomIngredient | undefined;
  reload: () => Promise<void>;
  reloadCustomIngredients: () => Promise<void>;
  clear: () => void;
  clearCustomIngredients: () => void;
  isDataLoadedFor: (restaurantId: string) => boolean;
  isCustomIngredientsLoadedFor: (restaurantId: string) => boolean;
  getUniqueCategories: () => string[];
  toggleCategoryExpansion: (categoryId: string) => void;
  isCategoryExpanded: (categoryId: string) => boolean;
}

let customLoadGeneration = 0;
let pendingCustomRestaurantId: string | null = null;
let automationRunLoadGeneration = 0;
let costPriceLoadGeneration = 0;
let pendingCostPriceRestaurantId: string | null = null;

export const useCostPriceStore = create<CostPriceStore>((set, get) => ({
  ingredientClipboard: null,
  setIngredientClipboard: (ingredientClipboard) => set({ ingredientClipboard }),
  automationRuns: [],
  automationRunsRestaurantId: null,
  automationRunsError: null,
  isLoadingAutomationRuns: false,
  loadAutomationRuns: async (restaurantId) => {
    const generation = ++automationRunLoadGeneration;
    set({ isLoadingAutomationRuns: true, automationRunsError: null,
      ...(get().automationRunsRestaurantId !== restaurantId ? { automationRuns: [], automationRunsRestaurantId: null } : {}),
    });
    try {
      const automationRuns = restaurantId === ALL_RESTAURANTS_ID ? [] : await getOpenAutomationRuns(restaurantId);
      if (generation !== automationRunLoadGeneration) return;
      set({ automationRuns, automationRunsRestaurantId: restaurantId, isLoadingAutomationRuns: false });
    } catch (error) {
      if (generation !== automationRunLoadGeneration) return;
      console.error('Failed to load automation runs:', error);
      set({ isLoadingAutomationRuns: false, automationRunsError: 'load_failed' });
    }
  },
  removeAutomationRun: (id) => set(state => ({ automationRuns: state.automationRuns.filter(run => run.id !== id) })),
  patchOne: (id, partial) => set(state => ({ costPriceList: state.costPriceList.map(dish => dish.id === id ? { ...dish, ...partial } : dish) })),
  costPriceList: [],
  customIngredients: [],
  isLoading: false,
  isLoadingCustomIngredients: false,
  error: null,
  customIngredientsError: null,
  currentRestaurantId: null,
  customIngredientsRestaurantId: null,
  expandedCategories: new Set<string>(),

  // Active tab for cost price page
  activeTab: 'all',
  setActiveTab: (tab) => set({ activeTab: tab }),

  // Add ingredient filters
  addIngredientSupplierFilters: [],
  setAddIngredientSupplierFilters: (filters) => set({ addIngredientSupplierFilters: filters }),
  addIngredientCategoryFilters: [],
  setAddIngredientCategoryFilters: (filters) => set({ addIngredientCategoryFilters: filters }),

  // Shared source-restaurant selection for the add-ingredient picker.
  addIngredientSourceRestaurantIds: [],
  setAddIngredientSourceRestaurantIds: (ids) => set({ addIngredientSourceRestaurantIds: ids }),

  load: async (restaurantId: string, options) => {
    const currentState = get();
    if (currentState.isLoading && pendingCostPriceRestaurantId === restaurantId && !options?.force) return;
    const generation = ++costPriceLoadGeneration;
    pendingCostPriceRestaurantId = restaurantId;
    
    set({ isLoading: true, error: null });
    
    try {
      let analyses: CostPriceAnalysis[];
      
      // Check if "All Restaurants" is selected
      if (restaurantId === ALL_RESTAURANTS_ID) {
        analyses = await fetchCostPriceAnalysesForAllRestaurants();
      } else {
        analyses = await fetchCostPriceAnalysesByRestaurant(restaurantId);
      }
      
      if (generation !== costPriceLoadGeneration) return;
      if (options?.isCurrent && !options.isCurrent()) {
        set({ isLoading: false });
        return;
      }
      set({ 
        costPriceList: analyses,
        currentRestaurantId: restaurantId,
        isLoading: false,
        error: null
      });
    } catch (error) {
      if (generation !== costPriceLoadGeneration) return;
      if (options?.isCurrent && !options.isCurrent()) {
        set({ isLoading: false });
        return;
      }
      console.error('Failed to load cost price analyses:', error);
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to load cost price analyses'
      });
    }
  },
  
  byId: (id: string) => {
    const state = get();
    return state.costPriceList.find(analysis => analysis.id === id);
  },

  // Replace a single item in the list (or append if new) without a full refetch.
  // Used after in-modal edits/deletes — the update API already returns the fresh
  // analysis. PUT /update does not return restaurantName (only the all-restaurants
  // fetch adds it), so preserve it from the existing item.
  upsertOne: (analysis: CostPriceAnalysis) => {
    set(state => {
      const existing = state.costPriceList.find(a => a.id === analysis.id);
      const merged = existing?.restaurantName && !analysis.restaurantName
        ? { ...analysis, restaurantName: existing.restaurantName }
        : analysis;
      const costPriceList = existing
        ? state.costPriceList.map(a => (a.id === analysis.id ? merged : a))
        : [...state.costPriceList, merged];
      return { costPriceList };
    });
  },

  removeOne: (id: string) => {
    set(state => ({ costPriceList: state.costPriceList.filter(a => a.id !== id) }));
  },

  reload: async () => {
    const state = get();
    const restaurantId = state.currentRestaurantId ?? state.costPriceList[0]?.restaurantId;
    if (restaurantId) {
      // Preserve the all-restaurants scope and supersede reads started before an edit.
      await get().load(restaurantId, { force: true });
    }
  },
  
  clear: () => {
    automationRunLoadGeneration++;
    costPriceLoadGeneration++;
    pendingCostPriceRestaurantId = null;
    set({ 
      ingredientClipboard: null,
      automationRuns: [],
      automationRunsRestaurantId: null,
      automationRunsError: null,
      isLoadingAutomationRuns: false,
      costPriceList: [],
      currentRestaurantId: null,
      isLoading: false,
      error: null
    });
  },

  isDataLoadedFor: (restaurantId: string) => {
    const state = get();
    return state.currentRestaurantId === restaurantId && !state.error;
  },

  getUniqueCategories: () => {
    const state = get();
    const categoryIds = state.costPriceList
      .map(analysis => analysis.categoryId)
      .filter((categoryId): categoryId is string => Boolean(categoryId));

    return Array.from(new Set(categoryIds));
  },

  loadCustomIngredients: async (restaurantId: string, options) => {
    if (get().isLoadingCustomIngredients && pendingCustomRestaurantId === restaurantId && !options?.force) return;
    const generation = ++customLoadGeneration;
    pendingCustomRestaurantId = restaurantId;
    set({ isLoadingCustomIngredients: true, customIngredientsError: null,
      ...(get().customIngredientsRestaurantId !== restaurantId ? { customIngredients: [], customIngredientsRestaurantId: null } : {}),
    });

    try {
      const ingredients = await fetchCustomIngredientsByRestaurant(restaurantId);
      if (generation !== customLoadGeneration) return;
      set({
        customIngredients: ingredients,
        customIngredientsRestaurantId: restaurantId,
        isLoadingCustomIngredients: false,
        customIngredientsError: null
      });
    } catch (error) {
      if (generation !== customLoadGeneration) return;
      console.error('Failed to load custom ingredients:', error);
      set({
        isLoadingCustomIngredients: false,
        customIngredientsError: error instanceof Error ? error.message : 'Failed to load custom ingredients'
      });
    }
  },

  customIngredientById: (id: string) => {
    const state = get();
    return state.customIngredients.find(ingredient => ingredient.id === id);
  },

  reloadCustomIngredients: async () => {
    const state = get();
    if (state.customIngredientsRestaurantId) {
      await get().loadCustomIngredients(state.customIngredientsRestaurantId);
    }
  },

  clearCustomIngredients: () => {
    customLoadGeneration++;
    pendingCustomRestaurantId = null;
    set({
      customIngredients: [],
      customIngredientsRestaurantId: null,
      isLoadingCustomIngredients: false,
      customIngredientsError: null
    });
  },

  isCustomIngredientsLoadedFor: (restaurantId: string) => {
    const state = get();
    return state.customIngredientsRestaurantId === restaurantId;
  },

  toggleCategoryExpansion: (categoryId: string) => {
    const state = get();
    const newExpandedCategories = new Set(state.expandedCategories);

    if (newExpandedCategories.has(categoryId)) {
      newExpandedCategories.delete(categoryId);
    } else {
      newExpandedCategories.add(categoryId);
    }

    set({ expandedCategories: newExpandedCategories });
  },

  isCategoryExpanded: (categoryId: string) => {
    const state = get();
    return state.expandedCategories.has(categoryId);
  }
}));
